import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

// TimeControl component for time input with arrows
const TimeControl = ({ value, onChange, error, label }) => {
  const [timeValue, setTimeValue] = useState(value || '08:00');

  useEffect(() => {
    if (value) {
      setTimeValue(value);
    } else if (!timeValue) {
      // Set default time if no value is provided and timeValue is not set
      const defaultTime = '08:00';
      setTimeValue(defaultTime);
      // Notify parent component about the default time
      onChange?.(defaultTime);
    }
  }, [value, timeValue, onChange]);

  const updateTime = (newTime) => {
    setTimeValue(newTime);
    onChange?.(newTime);
  };

  const handleHourChange = (newHour) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const hourValue = Math.max(0, Math.min(23, parseInt(newHour) || 0));
    const newTime = `${String(hourValue).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    updateTime(newTime);
  };

  const handleMinuteChange = (newMinute) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const minuteValue = Math.max(0, Math.min(59, parseInt(newMinute) || 0));
    const newTime = `${String(hours).padStart(2, '0')}:${String(minuteValue).padStart(2, '0')}`;
    updateTime(newTime);
  };

  const incrementHour = () => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const newHours = (hours + 1) % 24;
    const newTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    updateTime(newTime);
  };

  const decrementHour = () => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const newHours = (hours - 1 + 24) % 24;
    const newTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    updateTime(newTime);
  };

  const incrementMinutes = () => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    let newMinutes = minutes + 15; // 15-minute increments
    let newHours = hours;
    
    if (newMinutes >= 60) {
      newMinutes = 0;
      newHours = (newHours + 1) % 24;
    }
    
    const newTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    updateTime(newTime);
  };

  const decrementMinutes = () => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    let newMinutes = minutes - 15; // 15-minute decrements
    let newHours = hours;
    
    if (newMinutes < 0) {
      newMinutes = 45;
      newHours = (newHours - 1 + 24) % 24;
    }
    
    const newTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    updateTime(newTime);
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-foreground">{label}</label>
      )}
      <div className="flex items-center space-x-2">
        {/* Hours */}
        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={incrementHour}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-primary/10 hover:border-primary/30 transition-colors active:scale-95"
            aria-label="Increase hour"
          >
            <Icon name="ChevronUp" size={16} className="text-foreground" />
          </button>
          <input
            type="number"
            min="0"
            max="23"
            value={timeValue.split(':')[0]}
            onChange={(e) => handleHourChange(e.target.value)}
            className="w-12 h-10 border border-border rounded-md bg-input text-center font-mono text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <button
            type="button"
            onClick={decrementHour}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-primary/10 hover:border-primary/30 transition-colors active:scale-95"
            aria-label="Decrease hour"
          >
            <Icon name="ChevronDown" size={16} className="text-foreground" />
          </button>
        </div>

        {/* Separator */}
        <div className="text-2xl font-bold text-foreground">:</div>

        {/* Minutes */}
        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={incrementMinutes}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-primary/10 hover:border-primary/30 transition-colors active:scale-95"
            aria-label="Increase minutes by 15"
          >
            <Icon name="ChevronUp" size={16} className="text-foreground" />
          </button>
          <input
            type="number"
            min="0"
            max="59"
            value={timeValue.split(':')[1]}
            onChange={(e) => handleMinuteChange(e.target.value)}
            className="w-12 h-10 border border-border rounded-md bg-input text-center font-mono text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <button
            type="button"
            onClick={decrementMinutes}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-primary/10 hover:border-primary/30 transition-colors active:scale-95"
            aria-label="Decrease minutes by 15"
          >
            <Icon name="ChevronDown" size={16} className="text-foreground" />
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

const CorrectionModal = ({ 
  isOpen, 
  onClose, 
  entry, 
  onSave,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    clockIn: '',
    clockOut: '',
    dauer: 0,
    dauerS: 0,
    dauerT: 0,
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const displayTime = (timeString) => {
    if (!timeString) return '--:--';
    const [h = '00', m = '00'] = timeString.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };



  useEffect(() => {
    if (entry && isOpen) {
      // For mini-job employees, use the real clock-out time (clockOutReal) for editing
      // For other employees, use the regular clockOut time
      const clockOutValue = entry?.employee?.hire_type === 'Mini-job' && entry?.clockOutReal 
        ? entry.clockOutReal 
        : entry?.clockOut || '';
      
      setFormData({
        date: entry?.date || '',
        clockIn: entry?.clockIn || '',
        clockOut: clockOutValue,
        dauer: entry?.dauer || 0,
        dauerS: entry?.dauerS || 0,
        dauerT: entry?.dauerT || 0,
        notes: entry?.notes || ''
      });
      setErrors({});
      setHasChanges(false);
    }
  }, [entry, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    
    // Clear specific field error
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Date validation
    if (!formData?.date) {
      newErrors.date = 'Das Datum ist erforderlich.';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Check if date is too far in the future (optional - limit to 7 days ahead)
      const maxFutureDate = new Date();
      maxFutureDate.setDate(maxFutureDate.getDate() + 7);
      
      if (selectedDate > maxFutureDate) {
        newErrors.date = 'Das Datum darf nicht mehr als 7 Tage in der Zukunft liegen.';
      }
      
      // Check if date is too far in the past (optional - limit to 90 days back)
      const minPastDate = new Date();
      minPastDate.setDate(minPastDate.getDate() - 90);
      
      if (selectedDate < minPastDate) {
        newErrors.date = 'Das Datum darf nicht mehr als 90 Tage in der Vergangenheit liegen.';
      }
    }

    // Time validation
    if (formData?.clockIn && formData?.clockOut) {
      const clockInTime = new Date(`2000-01-01T${formData.clockIn}`);
      const clockOutTime = new Date(`2000-01-01T${formData.clockOut}`);
      
      if (clockOutTime <= clockInTime) {
        newErrors.clockOut = 'Die Gehen-Zeit muss nach der Kommen-Zeit liegen.';
      }

      // Check for reasonable work duration (max 16 hours)
      const duration = (clockOutTime - clockInTime) / (1000 * 60 * 60);
      if (duration > 16) {
        newErrors.clockOut = 'Die Arbeitsdauer darf 16 Stunden nicht überschreiten.';
      }
      
      // Check for minimum work duration (at least 15 minutes)
      if (duration < 0.25) {
        newErrors.clockOut = 'Die Arbeitsdauer muss mindestens 15 Minuten betragen.';
      }
    }

    // Check if clock in is provided when clock out exists
    if (formData?.clockOut && !formData?.clockIn) {
      newErrors.clockIn = 'Die Kommen-Zeit ist erforderlich, wenn eine Gehen-Zeit angegeben ist.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const correctionData = {
      ...entry,
      ...formData,
      originalClockIn: entry?.clockIn,
      // For mini-job employees, use the real clock-out time as original
      // For other employees, use the regular clockOut time
      originalClockOut: entry?.employee?.hire_type === 'Mini-job' && entry?.clockOutReal 
        ? entry.clockOutReal 
        : entry?.clockOut,
      correctedAt: new Date()?.toISOString(),
      correctedBy: 'current-user-id', // This would come from auth context
      correctionReason: formData?.reason,
      correctionCategory: formData?.reasonCategory,
      // Use the corrected date from formData
      date: formData?.date || entry?.date || new Date().toISOString().split('T')[0]
    };

    try {
      await onSave(correctionData);
      // Modal should close automatically after successful save
      console.log('Correction saved successfully, modal should close');
    } catch (error) {
      console.error('Error saving correction:', error);
      // Don't close modal on error
    }
  };

  const calculateDuration = () => {
    if (!formData?.clockIn || !formData?.clockOut) return null;
    
    // Apply 08:00 minimum start time adjustment
    const [inH, inM] = formData.clockIn.split(':').map(Number);
    const [outH, outM] = formData.clockOut.split(':').map(Number);
    
    // Convert to minutes for easier calculation
    let clockInMinutes = inH * 60 + inM;
    let clockOutMinutes = outH * 60 + outM;
    
    // Apply minimum start time of 8:00 (480 minutes)
    const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
    if (clockInMinutes < minStartMinutes) {
      clockInMinutes = minStartMinutes;
    }
    
    // Apply maximum end time of 17:00 (1020 minutes)
    const maxEndMinutes = 17 * 60; // 17:00 = 1020 minutes
    if (clockOutMinutes > maxEndMinutes) {
      clockOutMinutes = maxEndMinutes;
    }
    
    // Calculate working time in minutes
    const workingMinutes = Math.max(0, clockOutMinutes - clockInMinutes);
    
    if (workingMinutes <= 0) return null;
    
    const hours = Math.floor(workingMinutes / 60);
    const minutes = workingMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  // Auto-calculate duration fields for all employees with 08:00 minimum adjustment
  useEffect(() => {
    if (formData?.clockIn && formData?.clockOut) {
      // Apply 08:00 minimum start time adjustment
      const [inH, inM] = formData.clockIn.split(':').map(Number);
      const [outH, outM] = formData.clockOut.split(':').map(Number);
      
      // Convert to minutes for easier calculation
      let clockInMinutes = inH * 60 + inM;
      let clockOutMinutes = outH * 60 + outM;
      
      // Apply minimum start time of 8:00 (480 minutes)
      const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
      if (clockInMinutes < minStartMinutes) {
        clockInMinutes = minStartMinutes;
      }
      
      // Apply maximum end time of 17:00 (1020 minutes)
      const maxEndMinutes = 17 * 60; // 17:00 = 1020 minutes
      if (clockOutMinutes > maxEndMinutes) {
        clockOutMinutes = maxEndMinutes;
      }
      
      // Calculate working time in minutes and convert to hours
      const workingMinutes = Math.max(0, clockOutMinutes - clockInMinutes);
      const totalHours = workingMinutes / 60;
      
      if (totalHours > 0) {
        const dauerTValue = parseFloat(totalHours.toFixed(2)); // Total hours
        
        // Calculate Dauer and Dauer S based on hire type
        let dauerValue = 0;
        let dauerSValue = 0;
        
        if (entry?.employee?.hireType === 'Vollzeit') {
          // For Vollzeit: Dauer = first 8h, Dauer S = rest (8 hours work day)
          dauerValue = parseFloat(Math.min(totalHours, 8).toFixed(2));
          dauerSValue = parseFloat(Math.max(0, totalHours - 8).toFixed(2));
        } else if (entry?.employee?.hireType === 'Mini-job') {
          // For Mini-job: Dauer = first 2h, Dauer S = rest
          dauerValue = parseFloat(Math.min(totalHours, 2).toFixed(2));
          dauerSValue = parseFloat(Math.max(0, totalHours - 2).toFixed(2));
        } else {
          // For other types, use total hours as Dauer
          dauerValue = dauerTValue;
          dauerSValue = 0;
        }
        
        // Only update if values are different to prevent infinite loops
        if (formData.dauer !== dauerValue || formData.dauerS !== dauerSValue || formData.dauerT !== dauerTValue) {
          setFormData(prev => ({
            ...prev,
            dauer: dauerValue,
            dauerS: dauerSValue,
            dauerT: dauerTValue
          }));
        }
      }
    }
  }, [formData.clockIn, formData.clockOut, entry?.employee?.hireType]);

  // Helper function to calculate dynamic pause based on hours worked
  const calculatePauseHours = (totalHours) => {
    if (totalHours <= 0) return 0;
    // 15 minutes (0.25 hours) pause for every 2 hours worked
    const pauseBlocks = Math.floor(totalHours / 2);
    const calculatedPause = pauseBlocks * 0.25;
    // Cap maximum pause at 1 hour
    return Math.min(calculatedPause, 1);
  };

  // Helper function to calculate Gehalt S for employees
  const calculateGehaltS = () => {
    if ((entry?.employee?.hireType === 'Mini-job' || entry?.employee?.hireType === 'Vollzeit') && formData?.dauerT) {
      // Calculate Gehalt S = (Dauer T - dynamic pause) / 8 × Tagesrate
      const tagesrate = entry?.employee?.tagesrate || 0;
      const pauseHours = calculatePauseHours(formData.dauerT);
      const effectiveHours = Math.max(0, formData.dauerT - pauseHours);
      return (effectiveHours / 8) * tagesrate;
    }
    return 0;
  };

  const getChangeIndicator = (field, originalValue) => {
    const currentValue = formData?.[field];
    if (currentValue !== originalValue) {
      return (
        <div className="flex items-center space-x-1 text-xs text-warning">
          <Icon name="AlertTriangle" size={12} />
          <span>Geändert</span>
        </div>
      );
    }
    return null;
  };

  const hasTimeChanges = () => {
    return formData?.date !== entry?.date ||
           formData?.clockIn !== entry?.clockIn || 
           formData?.clockOut !== entry?.clockOut ||
           formData?.dauerT !== entry?.dauerT ||
           formData?.dauerS !== entry?.dauerS ||
           formData?.notes !== entry?.notes;
  };

  const getTimeChangeSummary = () => {
    if (!hasTimeChanges()) return null;
    
    const changes = [];
    if (formData?.date !== entry?.date) {
      const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        const [y, m, d] = dateStr.split('-');
        return `${d}.${m}.${y}`;
      };
      changes.push(`Datum: ${formatDate(entry?.date)} → ${formatDate(formData?.date)}`);
    }
    if (formData?.clockIn !== entry?.clockIn) {
      changes.push(`Kommen: ${entry?.clockIn || '--:--'} → ${formData?.clockIn || '--:--'}`);
    }
    if (formData?.clockOut !== entry?.clockOut) {
      changes.push(`Gehen: ${entry?.clockOut || '--:--'} → ${formData?.clockOut || '--:--'}`);
    }
    if (formData?.dauerS !== entry?.dauerS) {
      changes.push(`Dauer S: ${entry?.dauerS || 0}h → ${formData?.dauerS || 0}h`);
    }
    if (formData?.dauerT !== entry?.dauerT) {
      changes.push(`Dauer T: ${entry?.dauerT || 0}h → ${formData?.dauerT || 0}h`);
    }
    
    return changes;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Edit" size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                Zeiteintrag korrigieren
              </h2>
              <p className="text-sm text-text-secondary">
                {entry?.employee?.name} - {entry?.date && new Date(entry.date)?.toLocaleDateString('de-DE')}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="text-text-secondary hover:text-text-primary"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Original vs Current Times */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-text-primary mb-3">Ursprünglicher Eintrag</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-text-secondary">Datum:</span>
                <span className="ml-2 text-text-primary">
                  {entry?.date ? new Date(entry.date).toLocaleDateString('de-DE') : '--'}
                </span>
              </div>
              <div>
                <span className="text-text-secondary">Kommen:</span>
                <span className="ml-2 text-text-primary">{displayTime(entry?.clockIn)}</span>
              </div>
              <div>
                <span className="text-text-secondary">Gehen:</span>
                <span className="ml-2 text-text-primary">{displayTime(entry?.clockOut)}</span>
              </div>
            </div>
            {entry?.totalHours && (
              <div className="mt-2 text-xs text-text-secondary">
                Ursprüngliche Dauer: {entry.totalHours} Stunden
              </div>
            )}
          </div>

          {/* Date Correction */}
          <div>
            <Input
              type="date"
              label="Datum"
              value={formData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              error={errors?.date}
              className="max-w-sm"
            />
            {getChangeIndicator('date', entry?.date)}
          </div>

            {/* Time Corrections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
               <TimeControl
                 label="Kommen-Zeit"
                 value={formData?.clockIn}
                 onChange={(value) => handleInputChange('clockIn', value)}
                 error={errors?.clockIn}
               />
               {getChangeIndicator('clockIn', entry?.clockIn)}
             </div>
            <div>
              <TimeControl
                label="Gehen-Zeit"
                value={formData?.clockOut}
                onChange={(value) => handleInputChange('clockOut', value)}
                error={errors?.clockOut}
              />
              {getChangeIndicator('clockOut', entry?.clockOut)}
            </div>
          </div>

          {/* Change Summary */}
          {/* {hasTimeChanges() && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Edit" size={16} className="text-warning" />
                <span className="text-sm font-medium text-text-primary">Zusammenfassung der Änderungen</span>
              </div>
              <div className="text-xs text-text-secondary space-y-1">
                {getTimeChangeSummary()?.map((change, index) => (
                  <div key={index}>{change}</div>
                ))}
              </div>
            </div>
          )} */}


          {/* Dauer Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Dauer field - Display only for Mini-job, calculated dynamically */}
            {entry?.employee?.hireType === 'Mini-job' ? (
              <div>
                <label className="text-sm font-medium leading-none mb-2 block text-foreground">
                  Dauer (Stunden)
                </label>
                <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {formData?.clockIn && formData?.clockOut ? (
                    `${Math.floor(Math.min(2, formData.dauerT))}h ${Math.round((Math.min(2, formData.dauerT) - Math.floor(Math.min(2, formData.dauerT))) * 60)}m`
                  ) : (
                    '0h 0m'
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Automatisch berechnet: erste 2 Stunden
                </p>
              </div>
            ) : (
              <Input
                label="Dauer (Stunden)"
                type="number"
                step="0.01"
                min="0"
                value={formData?.dauer}
                onChange={(e) => handleInputChange('dauer', parseFloat(e?.target?.value) || 0)}
                error={errors?.dauer}
              />
            )}
            <Input
              label="Dauer S (Stunden)"
              type="number"
              step="0.01"
              min="0"
              value={formData?.dauerS}
              onChange={(e) => handleInputChange('dauerS', parseFloat(e?.target?.value) || 0)}
              error={errors?.dauerS}
              description={entry?.employee?.hireType === 'Mini-job' ? 'Automatisch berechnet: verbleibende Stunden' : undefined}
              disabled={entry?.employee?.hireType === 'Mini-job'}
            />
            <Input
              label="Dauer T (Stunden)"
              type="number"
              step="0.01"
              min="0"
              value={formData?.dauerT}
              onChange={(e) => handleInputChange('dauerT', parseFloat(e?.target?.value) || 0)}
              error={errors?.dauerT}
              description={entry?.employee?.hireType === 'Mini-job' ? 'Automatisch berechnet: Gesamtstunden' : undefined}
              disabled={entry?.employee?.hireType === 'Mini-job'}
            />
          </div>

          {/* Notes Field */}
          <div>
            <label className="text-sm font-medium leading-none mb-2 block text-foreground">
              Notizen
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              placeholder="Zusätzliche Notizen zu diesem Zeiteintrag..."
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              rows={3}
            />
            {getChangeIndicator('notes', entry?.notes)}
          </div>

          {/* Mini-job Duration Explanation */}
          {entry?.employee?.hireType === 'Mini-job' && (formData?.clockIn && formData?.clockOut) && (
            <div className="bg-info/5 border border-info/20 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Info" size={16} className="text-info" />
                <span className="text-sm font-medium text-text-primary">
                  Minijob-Dauer & Gehaltsberechnung
                </span>
              </div>
              <div className="text-xs text-text-secondary space-y-1">
                <div>Dauer (Erste 2 Std.): {Math.floor(Math.min(2, formData.dauerT))}h {Math.round((Math.min(2, formData.dauerT) - Math.floor(Math.min(2, formData.dauerT))) * 60)}m</div>
                <div>Dauer S (Verbleibende Std.): {Math.floor(Math.max(0, formData.dauerT - 2))}h {Math.round((Math.max(0, formData.dauerT - 2) - Math.floor(Math.max(0, formData.dauerT - 2))) * 60)}m</div>
                <div>Dauer T (Gesamt): {Math.floor(formData.dauerT)}h {Math.round((formData.dauerT - Math.floor(formData.dauerT)) * 60)}m</div>
                {formData?.dauerT > 0 && (
                  <div className="mt-2 p-2 bg-primary/10 rounded">
                    <div className="font-medium text-text-primary">Gehalt S Berechnung:</div>
                    <div>
                      {entry?.employee?.hireType === 'Vollzeit' ? (
                        <>
                          <div>Dauer: {formData.dauer?.toFixed(2)}h (erste 8,5 Std.)</div>
                          <div>Dauer S: {formData.dauerS?.toFixed(2)}h (Überstunden)</div>
                          <div>Gesamt: ({formData.dauerT?.toFixed(2)}h - {Math.round(calculatePauseHours(formData.dauerT) * 60)}min Pause) ÷ 8h × €{entry?.employee?.tagesrate || 0} = €{calculateGehaltS()?.toFixed(2)}</div>
                        </>
                      ) : (
                        <>
                          <div>Dauer: {formData.dauer?.toFixed(2)}h (erste 2 Std. für Minijob)</div>
                          <div>Dauer S: {formData.dauerS?.toFixed(2)}h (Überstunden)</div>
                          <div>Gesamt: ({formData.dauerT?.toFixed(2)}h - {Math.round(calculatePauseHours(formData.dauerT) * 60)}min Pause) ÷ 8h × €{entry?.employee?.tagesrate || 0} = €{calculateGehaltS()?.toFixed(2)}</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-xs text-text-secondary">
            Alle Korrekturen werden mit Zeitstempel und Benutzeridentität im Audit-Trail protokolliert.
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={loading}
              disabled={!hasChanges}
              iconName="Save"
              iconPosition="left"
            >
              Korrektur speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrectionModal;